import {
  ItineraryEvent,
  ItineraryEventType,
  ItineraryStop,
  JobType,
  StopType,
} from 'core/graphql/types';
import useCountryContext from 'core/hooks/useCountryContext';
import { format, parseISO } from 'date-fns';

export type PopupContentProps = {
  itineraryStop: ItineraryStop;
  order: number;
  events: ItineraryEvent[];
};

const PopupContent: React.FC<PopupContentProps> = ({
  itineraryStop,
  order,
  events,
}: PopupContentProps) => {
  const { countryContext } = useCountryContext();

  const renderAwb = (awbId: string, awb: string | null) => {
    let className = 'text-red';
    let title = 'Pending delivery';

    const event = events.find((x) => x.identifier.toString() === awbId);
    if (event && event.eventType === ItineraryEventType.Cancellation) {
      className = 'text-yellow';
      title = 'Canceled delivery';
    }
    if (event && event.eventType === ItineraryEventType.Delivery) {
      className = 'text-green';
      title = 'Finished delivery';
    }
    if (event && event.eventType === ItineraryEventType.C2CPickup) {
      className = 'text-green';
      title = 'Finished C2C pickup';
    }
    const isRetour =
      event &&
      (event.eventType === ItineraryEventType.StandardRetour ||
        event.eventType === ItineraryEventType.PickPackRetour);
    if (isRetour) {
      className = 'text-magenta';
      title = 'Finished R30 retour';
    }

    let displayText = '';
    if (awb) {
      displayText = awb;
    } else if (event?.identifierDescription) {
      displayText = event.identifierDescription;
    } else {
      displayText = awbId;
    }
    return (
      <li key={awbId}>
        <a
          href={`https://logistics.sameday.${countryContext}/awb/information/${awbId}`}
          target="_blank"
          rel="noreferrer"
        >
          <span className={className} title={title}>
            {displayText}
            {event && event.isLocationApproximated && (
              <span className="ml-1" title="Location is approximated">
                Approximated
              </span>
            )}
            {event && event.isManuallyGeocoded && (
              <>
                {event.isLocationApproximated && (
                  <span className="ml-1">-</span>
                )}
                <span className="ml-1" title="Location is manually geocoded">
                  Manual Geocode
                </span>
              </>
            )}
            {isRetour && (
              <span className="ml-1" title="R30 Retour">
                R30 Retour
              </span>
            )}
          </span>
        </a>
      </li>
    );
  };

  const renderAwbs = (stop: ItineraryStop) => {
    const identifiers = stop.identifiers?.split(',');
    const identifiersDescription = stop.identifiersDescriptions?.split(',');
    if (
      identifiers &&
      identifiersDescription &&
      identifiers.length === identifiersDescription.length
    ) {
      return identifiers?.map((identifier, index) =>
        renderAwb(identifier, identifiersDescription[index]),
      );
    }

    return identifiers?.map((identifier) => renderAwb(identifier, null));
  };

  const renderPickup = (identifier: string) => {
    let className = 'text-red';
    let title = 'Pending pickup';

    const event = events.find((x) => x.identifier.toString() === identifier);
    if (event && event.eventType === ItineraryEventType.Pickup) {
      className = 'text-blue';
      title = 'Finished pickup';
    }
    return (
      <div className={className} title={title}>
        {event?.pickedUpAwbNumbers && (
          <ul>
            {event.pickedUpAwbNumbers.split(',').map((awb) => (
              <ul>{awb}</ul>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderLockerVisit = (identifier: string) => {
    let className = 'text-red';
    let title = 'Pending locker visit';

    const event = events.find((x) => x.identifier.toString() === identifier);
    if (event && event.eventType === ItineraryEventType.LockerVisit) {
      className = 'text-blue';
      title = 'Finished locker visit';
    }
    return (
      <div className={className} title={title}>
        {event && <span>Locker visit completed</span>}
      </div>
    );
  };

  const renderPudoVisit = (identifier: string) => {
    let className = 'text-red';
    let title = 'Pending PUDO visit';

    const event = events.find((x) => x.identifier.toString() === identifier);
    if (event && event.eventType === ItineraryEventType.PudoVisit) {
      className = 'text-orange';
      title = 'Finished PUDO visit';
    }
    return (
      <div className={className} title={title}>
        {event && <span className={className}>PUDO visit completed</span>}
      </div>
    );
  };

  const renderCutOffInterval = (identifier: string | undefined) => {
    const event = events.find((x) => x.identifier.toString() === identifier);

    return (
      <div>
        {event?.visitTimeWindowStart && event.visitTimeWindowEnd && (
          <div>
            <span className="mr-2">
              <strong>Cut Off Interval</strong>
            </span>
            <span className="mr-1">
              {format(parseISO(event.visitTimeWindowStart), 'HH:mm')}
            </span>
            <span className="mr-1">-</span>
            <span>{format(parseISO(event.visitTimeWindowEnd), 'HH:mm')}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="px-2">
      <div className="text-lg">
        <span>Stop</span>
        <span className="ml-1">{order}</span>
      </div>
      {itineraryStop.stopType === StopType.Delivery && (
        <div>
          <span>Notification</span>
          {itineraryStop.notificationIntervalStart && (
            <span className="ml-1">
              {format(
                parseISO(itineraryStop.notificationIntervalStart),
                'HH:mm',
              )}
            </span>
          )}
          <span className="ml-1">-</span>
          {itineraryStop.notificationIntervalStop && (
            <span className="ml-1">
              {format(
                parseISO(itineraryStop.notificationIntervalStop),
                'HH:mm',
              )}
            </span>
          )}
        </div>
      )}

      <div>
        <span>Arrival</span>
        <span className="ml-1">
          {itineraryStop.arrival
            ? format(parseISO(itineraryStop.arrival), 'HH:mm')
            : '-'}
        </span>
      </div>
      <div>
        <span>Departure</span>
        <span className="ml-1">
          {itineraryStop.departure
            ? format(parseISO(itineraryStop.departure), 'HH:mm')
            : '-'}
        </span>
      </div>
      <div>
        <span>Address</span>
        <span className="ml-1">{itineraryStop.address}</span>
      </div>
      <p>
        <span>Coordinates</span>
        <span className="ml-1">{itineraryStop.latitude}</span>
        <span className="ml-1">{itineraryStop.longitude}</span>
      </p>
      <span>
        <a
          href={`https://www.google.com/maps/place/${itineraryStop.latitude},${itineraryStop.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue"
        >
          See location in Google Maps
        </a>
      </span>
      <br />
      {itineraryStop.stopType === StopType.Locker ? (
        <span>
          <strong>{`Locker - ${itineraryStop.identifiersDescriptions}`}</strong>
          <ul>
            {itineraryStop.identifiers
              ?.split(',')
              .map((identifier) => renderLockerVisit(identifier))}
          </ul>
        </span>
      ) : itineraryStop.stopType === StopType.Pudo ? (
        <span>
          <strong>{`PUDO - ${itineraryStop.identifiersDescriptions}`}</strong>
          <ul>
            {itineraryStop.identifiers
              ?.split(',')
              .map((identifier) => renderPudoVisit(identifier))}
          </ul>
        </span>
      ) : itineraryStop.jobType === JobType.Delivery ? (
        <span>
          <strong>Awbs</strong>
          <ul>{renderAwbs(itineraryStop)}</ul>
        </span>
      ) : (
        <span>
          <strong>
            {`Pickup Point Alias - ${itineraryStop.identifiersDescriptions}`}
          </strong>
          {renderCutOffInterval(itineraryStop.identifiers?.split(',')[0])}
          <ul>
            {itineraryStop.identifiers
              ?.split(',')
              .map((awb) => renderPickup(awb))}
          </ul>
        </span>
      )}
    </div>
  );
};

export default PopupContent;
